import { useState } from 'react';
import { useInView } from '@/hooks/useInView';
import {
  ChevronRight, MapPin, Clock, Briefcase, Users, Heart,
  GraduationCap, Shield, TrendingUp, ArrowRight, Star,
} from 'lucide-react';
import { useDynamicContent, usePublicSeo, usePublicJobs } from '@/hooks/useDynamicContent';
import { resolveIcon } from '@/utils/iconMap';
import JobApplicationModal from '@/components/JobApplicationModal';
import JobDetailsModal from '@/components/JobDetailsModal';
import Seo from '@/seo/Seo';

const TEAM_IMG = 'https://images.unsplash.com/photo-1758520145140-c2dd8e78fc02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbW9kZXJuJTIwb2ZmaWNlJTIwdGVjaG5vbG9neSUyMHRlYW18ZW58MHx8fHwxNzc1OTU2NDc5fDA&ixlib=rb-4.1.0&q=85';

const DEFAULTS_CONTENT = {
  hero_title: 'Build Your Career.',
  hero_title_gold: 'Support Real Operations.',
  hero_description: "We're looking for professionals who take their work seriously and want to make a real contribution. At Score Integrated Solutions, you'll support technology and operational environments for the U.S. Army, DHA, DLA, VA, and other federal organizations.",
  culture_heading: 'More Than a Job. Real Work That Matters.',
  culture_p1: "At Score Integrated Solutions, the work is real and the impact is tangible. You won't be writing proposals nobody reads or building things nobody uses. You'll be supporting live operational environments where reliability and follow-through matter every day.",
  culture_p2: "We invest in our people because they are the foundation of what we deliver. We keep teams small enough to communicate clearly and structured enough to operate professionally. If you're someone who takes pride in doing good work, you'll fit in here.",
  benefits_heading: 'What We Offer',
  benefits: [
    { icon: 'Heart', title: 'Comprehensive Benefits', desc: 'Health, dental, vision insurance, and competitive retirement plans.' },
    { icon: 'TrendingUp', title: 'Career Growth', desc: 'Professional development, certifications, and clear advancement paths.' },
    { icon: 'GraduationCap', title: 'Continuous Learning', desc: 'Training allowances, conference attendance, and mentorship programs.' },
    { icon: 'Shield', title: 'Mission Impact', desc: 'Work on projects that directly support national security and defense.' },
    { icon: 'Users', title: 'Team Culture', desc: 'Collaborative, supportive environment with experienced professionals.' },
    { icon: 'Star', title: 'Work-Life Balance', desc: 'Flexible schedules, remote work options, and generous PTO.' },
  ],
  openings_heading: 'Current Openings',
  openings_intro: "We're always looking for talented professionals. Explore our current openings or send us your resume for future opportunities.",
  no_openings_message: 'No open positions are currently posted. Please check back soon or submit your resume for future opportunities.',
  general_cta_heading: "Don't See Your Role?",
  general_cta_text: "We're always interested in hearing from talented professionals. Send us your resume and let us know how you can contribute to our mission.",
};

void [Briefcase, Users, Heart, GraduationCap, Shield, TrendingUp, Star];

function Section({ children, className = '', id }) {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      id={id}
      className={`${className} transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      {children}
    </section>
  );
}

export default function CareersPage() {
  const c = useDynamicContent('careers', DEFAULTS_CONTENT);
  const seoOverrides = usePublicSeo('careers');
  const openings = usePublicJobs([]);

  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [applyJob, setApplyJob] = useState(null);
  const [showApply, setShowApply] = useState(false);

  const openDetails = (job) => { setSelectedJob(job); setShowDetails(true); };
  const closeDetails = () => setShowDetails(false);

  const openApply = (job) => {
    setShowDetails(false);
    setApplyJob(job || null);
    setShowApply(true);
  };
  const closeApply = () => setShowApply(false);

  const handleViewOpenings = (e) => {
    e.preventDefault();
    const el = document.getElementById('open-positions');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#121212]">
      <Seo
        page="careers"
        title={seoOverrides?.title}
        description={seoOverrides?.description}
        keywords={seoOverrides?.keywords}
        ogTitle={seoOverrides?.og_title}
        ogDescription={seoOverrides?.og_description}
      />
      <section data-testid="careers-hero" className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={TEAM_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/60 via-[#121212]/85 to-[#121212]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="inline-block text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold mb-4 border border-[#F2B705]/30 px-4 py-1.5">Join Our Team</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-6">
            {c.hero_title}<br />
            <span className="gold-gradient-text">{c.hero_title_gold}</span>
          </h1>
          <p className="text-base md:text-lg text-[#A3A3A3] max-w-2xl leading-relaxed mb-10">{c.hero_description}</p>
          <a
            href="#open-positions"
            onClick={handleViewOpenings}
            data-testid="careers-view-openings-btn"
            className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-8 py-4 hover:bg-[#D9A404] transition-colors inline-flex items-center gap-2"
          >
            View Open Positions <ChevronRight size={16} />
          </a>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F2B705]/40 to-transparent" />
      </section>

      {/* Culture */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-px bg-[#F2B705]" />
                <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Why Score Integrated Solutions</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">{c.culture_heading}</h2>
              <div className="space-y-4 text-sm text-[#A3A3A3] leading-relaxed">
                <p>{c.culture_p1}</p>
                <p>{c.culture_p2}</p>
              </div>
            </div>
            <div className="relative">
              <div className="border border-white/10 overflow-hidden">
                <img src={TEAM_IMG} alt="Team collaboration" className="w-full h-64 object-cover" />
              </div>
              <div className="absolute -top-3 -right-3 w-24 h-24 border-t-2 border-r-2 border-[#F2B705]/40" />
              <div className="absolute -bottom-3 -left-3 w-24 h-24 border-b-2 border-l-2 border-[#F2B705]/40" />
              <div className="border border-white/10 overflow-hidden mt-4">
                <img src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHRlY2hub2xvZ3klMjBjb25zdWx0aW5nJTIwdGVhbSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzU5NTcxODl8MA&ixlib=rb-4.1.0&q=85" alt="Business meeting" className="w-full h-48 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Benefits */}
      <Section className="py-24 md:py-32 bg-[#161616] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F2B705]/3 rounded-full blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#F2B705]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Benefits & Perks</span>
              <div className="w-12 h-px bg-[#F2B705]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">{c.benefits_heading}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(c.benefits || []).map((b, i) => {
              const Icon = resolveIcon(b.icon, Heart);
              return (
                <div key={b.title || i} data-testid={`benefit-card-${i}`} className="bg-[#121212] border border-white/10 p-8 card-hover gold-border-glow transition-all group relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-[#F2B705]/0 group-hover:bg-[#F2B705] transition-all" />
                  <Icon className="text-[#F2B705] mb-4" size={24} strokeWidth={1.5} />
                  <h3 className="text-sm font-semibold text-white mb-2">{b.title}</h3>
                  <p className="text-xs text-[#A3A3A3] leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* Image divider */}
      <section className="relative h-48 md:h-64 overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-2 gap-0">
          <img src="https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxzZXJ2ZXIlMjByb29tJTIwZGF0YSUyMGNlbnRlciUyMGNsb3VkfGVufDB8fHx8MTc3NTk1NzIwN3ww&ixlib=rb-4.1.0&q=85" alt="Server room" className="w-full h-full object-cover" />
          <img src="https://images.unsplash.com/photo-1585565931879-9bd1585ea40b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxtaWxpdGFyeSUyMHRlY2hub2xvZ3klMjBkZWZlbnNlJTIwY3liZXJzZWN1cml0eSUyMG9wZXJhdGlvbnN8ZW58MHx8fHwxNzc1OTU3MTk1fDA&ixlib=rb-4.1.0&q=85" alt="Tech operations" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#161616] via-transparent to-[#121212]" />
        <div className="absolute inset-0 bg-[#121212]/30" />
      </section>

      {/* Openings */}
      <Section className="py-24 md:py-32" id="open-positions">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-px bg-[#F2B705]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Open Positions</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">{c.openings_heading}</h2>
          <p className="text-sm text-[#A3A3A3] mb-12 max-w-xl">{c.openings_intro}</p>

          {openings.length === 0 ? (
            <div data-testid="no-openings" className="border border-white/10 bg-[#161616] p-10 md:p-12 text-center">
              <Briefcase className="text-[#F2B705] mx-auto mb-4" size={36} strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-white mb-3">No Open Positions Right Now</h3>
              <p className="text-sm text-[#A3A3A3] max-w-md mx-auto mb-6">
                {c.no_openings_message}
              </p>
              <button
                onClick={() => openApply(null)}
                data-testid="no-openings-apply-btn"
                className="bg-[#F2B705] text-black font-bold text-xs uppercase tracking-wide px-8 py-3 hover:bg-[#D9A404] transition-colors inline-flex items-center gap-2"
              >
                Submit Your Resume <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {openings.map((job, i) => (
                <div
                  key={job.id || job.title || i}
                  data-testid={`job-card-${i}`}
                  onClick={() => openDetails(job)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetails(job); } }}
                  role="button"
                  tabIndex={0}
                  className="bg-[#161616] border border-white/10 p-6 md:p-8 group hover:border-[#F2B705]/30 transition-all relative overflow-hidden cursor-pointer focus:outline-none focus:border-[#F2B705]/50"
                >
                  <div className="absolute top-0 left-0 w-0 group-hover:w-1 h-full bg-[#F2B705] transition-all" />
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#F2B705] transition-colors">{job.title}</h3>
                      <p className="text-sm text-[#A3A3A3] leading-relaxed mb-3 line-clamp-2">{job.description || job.desc}</p>
                      <div className="flex flex-wrap gap-3">
                        {job.location && (
                          <span className="flex items-center gap-1.5 text-xs text-[#525252]">
                            <MapPin size={12} /> {job.location}
                          </span>
                        )}
                        {(job.employment_type || job.type) && (
                          <span className="flex items-center gap-1.5 text-xs text-[#525252]">
                            <Clock size={12} /> {job.employment_type || job.type}
                          </span>
                        )}
                        {job.clearance && (
                          <span className="flex items-center gap-1.5 text-xs text-[#525252]">
                            <Shield size={12} /> {job.clearance}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 self-start md:self-center">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openDetails(job); }}
                        data-testid={`job-view-${i}`}
                        className="text-xs uppercase tracking-wide font-bold text-[#A3A3A3] hover:text-white transition-colors"
                      >
                        View Details
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openApply(job); }}
                        data-testid={`job-apply-${i}`}
                        className="border border-[#F2B705]/30 text-[#F2B705] font-bold text-xs uppercase tracking-wide px-6 py-3 hover:bg-[#F2B705] hover:text-black transition-all inline-flex items-center gap-2"
                      >
                        Apply <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* General Application CTA */}
      <Section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="border border-[#F2B705]/20 p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#F2B705] to-transparent" />
            <Briefcase className="text-[#F2B705] mx-auto mb-6" size={32} strokeWidth={1.5} />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{c.general_cta_heading}</h2>
            <p className="text-sm text-[#A3A3A3] max-w-lg mx-auto mb-8">{c.general_cta_text}</p>
            <button
              type="button"
              onClick={() => openApply(null)}
              data-testid="careers-general-apply-btn"
              className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-10 py-4 hover:bg-[#D9A404] transition-colors inline-flex items-center gap-2"
            >
              Send Your Resume <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </Section>

      <JobDetailsModal open={showDetails} job={selectedJob} onClose={closeDetails} onApply={openApply} />
      <JobApplicationModal open={showApply} job={applyJob} onClose={closeApply} />
    </div>
  );
}
